import {announce} from '@github-ui/aria-live'
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'
import {CopilotPlan} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {Banner, Link, LinkButton} from '@primer/react'
import {useCallback, useEffect, useState} from 'react'
import type {AgentQuotaSnapshot} from '../../types/agent-quota-snapshot'
import styles from './QuotaBanner.module.css'
import {MANAGE_BUDGET_URL, UPGRADE_PLAN_URL} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import {formatQuotaResetDate, getQuotaBannerState, isHighSeverityApproachingLimit} from '../../utils/quota-banner-state'

type QuotaBannerProps = {
  agentQuotaSnapshot?: AgentQuotaSnapshot | null
}

// Pass an element so the ariaNotify polyfill can locate the correct live
// region root when the banner renders inside a dialog (the polyfill suppresses
// announcements from elements outside the active modal).
function useAnnounceMessage(message: string | null, assertive: boolean, element: HTMLElement | null) {
  useEffect(() => {
    if (message && element) {
      announce(message, {assertive, element})
    }
  }, [message, assertive, element])
}

export function QuotaBanner({agentQuotaSnapshot}: QuotaBannerProps) {
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null)
  const containerRef = useCallback((node: HTMLDivElement | null) => setContainerEl(node), [])
  const enabled = copilotFeatureFlags.codingAgentTBBQuotaBanner
  const canStartSession = agentQuotaSnapshot?.can_start_session ?? true
  const percentRemaining = agentQuotaSnapshot?.percent_remaining ?? 1

  const requestUsageMessage =
    agentQuotaSnapshot &&
    (agentQuotaSnapshot.copilot_plan === CopilotPlan.Business ||
      agentQuotaSnapshot.copilot_plan === CopilotPlan.Enterprise)
      ? ' Request your admin for additional usage.'
      : ''

  const shouldShowWarning = percentRemaining >= 0 && percentRemaining <= 0.25
  const warningMessage =
    percentRemaining <= 0.1
      ? 'You have used 90% of your monthly usage limit.'
      : 'You have used 75% of your monthly usage limit.'

  // Determine which message (if any) should be announced to screen readers.
  let announcementMessage: string | null = null
  let assertive = false
  if (enabled && agentQuotaSnapshot) {
    if (!canStartSession) {
      announcementMessage = `The remaining usage limit is insufficient to start a session.${requestUsageMessage}`
      assertive = true
    } else if (shouldShowWarning) {
      announcementMessage = warningMessage
    }
  }

  useAnnounceMessage(announcementMessage, assertive, containerEl)

  // Temporary gate the banner behind this FF until the use_token_based_billing logic is implemented.
  if (!enabled) return null

  // agentQuotaSnapshot isn't defined when the user has no agent access or quota info is unavailable
  if (!agentQuotaSnapshot) return null

  const state = getQuotaBannerState(agentQuotaSnapshot)

  switch (state.kind) {
    case 'cfiBlocked': {
      const resetDate = formatQuotaResetDate(state.quotaResetDate)
      const description = resetDate
        ? `You don't have enough AI credits to start or steer a session. Limit resets on ${resetDate}.`
        : "You don't have enough AI credits to start or steer a session."

      return (
        <div ref={containerRef} className={styles.container} data-testid="quota-banner">
          <Banner
            variant="critical"
            title="Insufficient AI credits"
            hideTitle
            description={description}
            primaryAction={
              <LinkButton as="a" href={MANAGE_BUDGET_URL}>
                Enable additional usage
              </LinkButton>
            }
            secondaryAction={
              state.canUpgradePlan ? (
                <Link as="a" href={UPGRADE_PLAN_URL}>
                  Upgrade plan
                </Link>
              ) : undefined
            }
          />
        </div>
      )
    }
    case 'cfiBudgetHit': {
      return (
        <div ref={containerRef} className={styles.container} data-testid="quota-banner">
          <Banner
            variant="critical"
            title="Additional usage budget reached"
            hideTitle
            description="You've run out of your budget for additional usage of AI credits."
            primaryAction={
              <LinkButton as="a" href={MANAGE_BUDGET_URL}>
                Increase budget
              </LinkButton>
            }
            secondaryAction={
              state.canUpgradePlan ? (
                <Link as="a" href={UPGRADE_PLAN_URL}>
                  Upgrade plan
                </Link>
              ) : undefined
            }
          />
        </div>
      )
    }
    case 'ceCbBlocked': {
      return (
        <div ref={containerRef} className={styles.container} data-testid="quota-banner">
          <Banner
            variant="critical"
            title="Insufficient AI credits"
            hideTitle
            description="You don't have enough AI credits to start or steer a session. Contact your administrator for more information."
          />
        </div>
      )
    }
    case 'approachingLimit': {
      const description = state.overageMode
        ? `You have used ${state.percentUsed}% of your additional AI credit usage.`
        : `You have used ${state.percentUsed}% of your included AI credits.`

      // CFI users can self-serve: surface budget management (and upgrade plan
      // when eligible). CB/CE users are managed by their org admin so we
      // render the warning without CTAs to match the rest of the spec.
      // When the user is consuming additional usage credits (overageMode),
      // swap "Manage budget" → "Increase budget" to match the CFI guide.
      const showBudgetCta = state.isIndividual
      const showUpgradePlan = state.isIndividual && state.canUpgradePlan

      const budgetAction = showBudgetCta ? (
        <LinkButton as="a" href={MANAGE_BUDGET_URL}>
          {state.overageMode ? 'Increase budget' : 'Manage budget'}
        </LinkButton>
      ) : undefined

      const upgradePlanAction = showUpgradePlan ? (
        <Link as="a" href={UPGRADE_PLAN_URL}>
          Upgrade plan
        </Link>
      ) : undefined

      return (
        <div ref={containerRef} className={styles.container} data-testid="quota-banner">
          <Banner
            variant={isHighSeverityApproachingLimit(state.percentUsed) ? 'warning' : 'info'}
            title="Usage limit warning"
            hideTitle
            description={description}
            primaryAction={budgetAction}
            secondaryAction={upgradePlanAction}
          />
        </div>
      )
    }
    case 'none':
    default:
      return null
  }
}
